import { Component, input, model, NgZone, signal, viewChild } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ChartComponent, ChartType } from 'ng-apexcharts';
import { of, switchMap } from 'rxjs';
import { NemChart } from '../../services/nem-chart';
import { takeSnapshot } from '../../services/take-snapshot';

@Component({
  selector: 'nem-chart',
  templateUrl: './nem-chart.comp.html',
  styles: [
    `
      ::ng-deep .snapshot-button {
        width: 1rem !important;
        height: 1rem !important;
        padding: 0.75rem !important;
      }
    `
  ],
  standalone: false
})
export class NemChartComponent {
  // Signal Inputs
  nemChart = input<NemChart | null>(null);
  previewMode = input<boolean>(false);

  // ViewChild Signals
  plot = viewChild<ChartComponent>('plot');
  plot_brush = viewChild<ChartComponent>('plot_brush');

  // UI State Signals
  resizing = model<boolean>(false);
  dialogVisible = signal<boolean>(false);
  capturePending = signal<boolean>(false);

  private _chart$ = toObservable(this.nemChart);

  chartValue = toSignal(this._chart$.pipe(switchMap(c => (c ? c.value$ : of(null)))));
  chartBrush = toSignal(this._chart$.pipe(switchMap(c => (c ? c.brush$ : of(null)))));
  titleFrequency = toSignal(this._chart$.pipe(switchMap(c => (c ? c.titleFrequency$ : of('')))), { initialValue: '' });
  titleRegion = toSignal(this._chart$.pipe(switchMap(c => (c ? c.titleRegion$ : of('')))), { initialValue: '' });

  constructor(private _router: Router, private _ngZone: NgZone) {}

  toggleTableView() {
    const chart = this.nemChart();
    if (!chart) return;
    chart.tabular.toggle();
    this.refresh();
  }

  chartTypeChange(chartType: ChartType | null) {
    const chart = this.nemChart();
    if (!chart) return;
    if (chartType) chart.chartType = chartType;
    chart.refereshFromCache('user');
  }

  async takeSnapshot(text: string | undefined, containerId: string) {
    this.capturePending.set(true);

    await new Promise(resolve => setTimeout(resolve, 50));

    this._ngZone.runOutsideAngular(async () => {
      try {
        await takeSnapshot({ text: text || '', containerId });
      } catch (err) {
        console.error('Erreur de capture', err);
      } finally {
        this._ngZone.run(() => {
          this.capturePending.set(false);
        });
      }
    });
  }

  redirectToFocusmode() {
    const chart = this.nemChart();
    if (!chart) return;
    this._router.navigate(chart.focusmodeLink);
  }

  onResizeStart() {
    this.resizing.set(true);
  }

  onResizeEnd() {
    this.resizing.set(false);
    this.refresh();
  }

  refresh() {
    this.plot()?.resetSeries();
    this.plot_brush()?.resetSeries();
  }
}
